import React from "react";
import Grid from "@mui/material/Grid";
import SloGaugeChart from "./SloGaugeChart";
import VersionChart from "./VersionChart";
import RelationChart from "./RelationChart";
import CriticalList from "./CriticalList";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { Title, useDataProvider } from "react-admin";
import SloMetChart from "./SloMetChart";
const Loading = () => <div>Loading...</div>;

const CveAnalytics: React.FC<{ permissions?: any }> = ({ permissions }) => {
  const [expanded, setExpanded] = React.useState(false);
  const handleToggleExpand = () => setExpanded((prev) => !prev);
  const [releaseName, setReleaseName] = React.useState<string | null>(null);
  const [releaseVersion, setReleaseVersion] = React.useState<string | null>(
    null
  );
  const [releaseChoices, setReleaseChoices] = React.useState<
    { id: string; name: string }[]
  >([]);
  const [versionChoices, setVersionChoices] = React.useState<
    { id: string; name: string }[]
  >([]);

  const dataProvider = useDataProvider();

  React.useEffect(() => {
    const fetchUniqueValues = async () => {
      try {
        const response = await dataProvider.getUnique("cve/releases", {
          field: "name",
        });
        const release = response[0];
        const releases = response.map((value: string) => ({
          id: value,
          name: value
            .replace("-", " ")
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
        }));
        setReleaseChoices(releases);
        let versions = await dataProvider.getUnique("cve/releases", {
          field: "version",
          filter: {
            name: release,
            $or: [
              {
                endOfMaintenanceDate: { $gte: new Date() },
              },
              { endOfMaintenanceDate: null },
            ],
          },
        });
        const latestVersion = versions[0];
        setVersionChoices(
          versions.map((value: string) => ({
            id: value,
            name: value,
          }))
        );
        setReleaseName(release);
        setReleaseVersion(latestVersion);
      } catch (error) {
        console.error("Error fetching unique values:", error);
      }
    };

    fetchUniqueValues();
  }, [dataProvider]);

  const fetchVersions = async (releaseName: string) => {
    try {
      const response = await dataProvider.getUnique("cve/releases", {
        field: "version",
        filter: {
          name: releaseName,
          $or: [
            {
              endOfMaintenanceDate: { $gte: new Date() },
            },
            { endOfMaintenanceDate: null },
          ],
        },
      });
      // correctly sort versions when using astro-runtime-v3 which uses format N.N-N
      // we need to sort by Major, Minor, then Patch numerically
      let versionChoices = response.map((value: string) => ({
        id: value,
        name: value,
      }));
      if (releaseName === "astro-runtime-v3") {
        versionChoices = response
          .sort((a: string, b: string) => {
            const [aMajorMinor, aPatch] = a.split("-");
            const [bMajorMinor, bPatch] = b.split("-");
            const [aMajor, aMinor] = aMajorMinor.split(".").map(Number);
            const [bMajor, bMinor] = bMajorMinor.split(".").map(Number);
            if (aMajor !== bMajor) return aMajor - bMajor;
            if (aMinor !== bMinor) return aMinor - bMinor;
            return parseInt(aPatch || "0") - parseInt(bPatch || "0");
          })
          .reverse()
          .map((value: string) => ({
            id: value,
            name: value,
          }));
      }
      setVersionChoices(versionChoices);
      setReleaseVersion(response[0]);
    } catch (error) {
      console.error("Error fetching unique values:", error);
    }
  };

  return (
    <div>
      <Title title="CVE Analytics" />
      {releaseName && (
        <Select
          value={releaseName}
          onChange={(e) => {
            if (!e.target.value) return;
            setReleaseName(e.target.value);
            setReleaseVersion(null);
            setVersionChoices([]);
            fetchVersions(e.target.value);
          }}
          label="Product"
          sx={{ width: "14em" }}
        >
          {releaseChoices.map((choice) => (
            <MenuItem key={choice.id} value={choice.id}>
              {choice.name}
            </MenuItem>
          ))}
        </Select>
      )}
      {releaseName && versionChoices.length > 0 && releaseVersion ? (
        <>
          <Select
            value={releaseVersion}
            onChange={(e) => {
              setReleaseVersion(e.target.value);
            }}
            label="Version"
            sx={{ width: "14em" }}
            defaultValue={releaseVersion}
          >
            {versionChoices.map((choice) => (
              <MenuItem key={choice.id} value={choice.id}>
                {choice.name}
              </MenuItem>
            ))}
          </Select>
          <Grid container spacing={3} sx={{ marginTop: "2em" }}>
            {expanded ? (
              <>
                <Grid size={{ xs: 12, md: 12, lg: 12 }} component="div">
                  <RelationChart
                    releaseName={releaseName}
                    releaseVersion={releaseVersion}
                    expanded={expanded}
                    onToggleExpand={handleToggleExpand}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 12, lg: 12 }} component="div">
                  <VersionChart releaseName={releaseName} />
                </Grid>
              </>
            ) : (
              <>
                <Grid size={{ xs: 12, md: 12, lg: 7 }} component="div">
                  <VersionChart releaseName={releaseName} />
                </Grid>
                <Grid size={{ xs: 12, md: 12, lg: 5 }} component="div">
                  <RelationChart
                    releaseName={releaseName}
                    releaseVersion={releaseVersion}
                    expanded={expanded}
                    onToggleExpand={handleToggleExpand}
                  />
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12, md: 6, lg: 3 }} component="div">
              <SloGaugeChart
                releaseName={releaseName}
                releaseVersion={releaseVersion}
                sloField="adjudicationSloDate"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 3 }} component="div">
              <SloMetChart
                releaseName={releaseName}
                releaseVersion={releaseVersion}
                sloField="adjudicationSloMet"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 3 }} component="div">
              <SloGaugeChart
                releaseName={releaseName}
                releaseVersion={releaseVersion}
                sloField="fixSloDate"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 3 }} component="div">
              <SloMetChart
                releaseName={releaseName}
                releaseVersion={releaseVersion}
                sloField="fixSloMet"
              />
            </Grid>
          </Grid>
          <CriticalList
            releaseName={releaseName}
            releaseVersion={releaseVersion}
          />
        </>
      ) : (
        <Loading />
      )}
    </div>
  );
};

export default CveAnalytics;
